import DeleteIcon from '@mui/icons-material/Delete';
import { Box, Chip, IconButton, Stack, Typography } from '@mui/material';
import { LightTooltip } from 'lib/designSystem/WhiteTooltip';
import React from 'react';

import { Aval } from '../types';

type Props = {
  aval: Aval;
  index: number;
  onDelete: (index: number) => void;
};

const AvalCard: React.FC<Props> = ({ aval, index, onDelete }) => {
  const getAvalTypeLabel = (aval: Aval) => {
    switch (aval.type) {
      case 'SAME_COMPANY':
        return 'Representante empresa';
      case 'OTHER_COMPANY':
        return `${aval.business?.name}`;
      case 'SPOUSE':
        return `Cónyuge ${aval.spouseRelation?.name}`;
    }
  };

  const renderTooltipContent = (aval: Aval) => {
    return (
      <Box sx={{ p: 2, minWidth: 250 }}>
        <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1.5 }}>
          Información del aval
        </Typography>
        <Stack spacing={1.5}>
          {aval.signer?.name && (
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: '0.75rem' }}
              >
                Nombre
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                {aval.signer.name}
              </Typography>
            </Box>
          )}
          {aval.signer?.identifier && (
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: '0.75rem' }}
              >
                RUT
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                {aval.signer.identifier}
              </Typography>
            </Box>
          )}
          {aval.signer?.email && (
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: '0.75rem' }}
              >
                Email
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                {aval.signer.email}
              </Typography>
            </Box>
          )}
          {aval.signer?.phone && (
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: '0.75rem' }}
              >
                Teléfono
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                {aval.signer.phone}
              </Typography>
            </Box>
          )}
          {aval.signer?.profession && (
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: '0.75rem' }}
              >
                Profesión
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                {aval.signer.profession}
              </Typography>
            </Box>
          )}
          {aval.type === 'SPOUSE' && aval.spouseRelation && (
            <Box
              sx={{
                mt: 1,
                pt: 1.5,
                borderTop: '1px solid rgba(0, 0, 0, 0.12)',
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: '0.75rem' }}
              >
                Relación
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                {aval.spouseRelation.name} (RUT:{' '}
                {aval.spouseRelation.identifier})
              </Typography>
            </Box>
          )}
          {aval.type === 'OTHER_COMPANY' && aval.business && (
            <Box
              sx={{
                mt: 1,
                pt: 1.5,
                borderTop: '1px solid rgba(0, 0, 0, 0.12)',
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: '0.75rem' }}
              >
                Empresa
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                {aval.business.name} (RUT: {aval.business.identifier})
              </Typography>
            </Box>
          )}
        </Stack>
      </Box>
    );
  };

  return (
    <LightTooltip
      key={`${aval.signer?.identifier}-${index}`}
      title={renderTooltipContent(aval)}
      placement="left"
      arrow
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          p: 1.5,
          borderRadius: 2,
          border: '1px solid rgba(0, 0, 0, 0.12)',
          backgroundColor: 'white',
          transition: 'all 0.2s ease-in-out',
          cursor: 'pointer',
          '&:hover': {
            borderColor: 'primary.main',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
            transform: 'translateY(-1px)',
          },
        }}
      >
        <IconButton
          aria-label="Eliminar aval"
          onClick={(e) => {
            e.stopPropagation();
            onDelete(index);
          }}
          size="small"
          sx={{
            backgroundColor: 'rgba(255, 0, 0, 0.08)',
            '&:hover': {
              backgroundColor: 'rgba(255, 0, 0, 0.15)',
            },
          }}
        >
          <DeleteIcon fontSize="small" color="error" />
        </IconButton>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            fontWeight="medium"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {aval.signer?.name || 'Sin nombre'}
          </Typography>
        </Box>
        <Chip
          label={getAvalTypeLabel(aval)}
          variant="outlined"
          size="small"
          color="primary"
          sx={{ flexShrink: 0 }}
        />
      </Box>
    </LightTooltip>
  );
};

export default AvalCard;
